import React from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "theme-ui";
import { themevals } from "theme/themevals";
import { PlanRow } from "api/Dashboard";
import { Panel, EmptyState, currency } from "./shared";

/**
 * Maintenance plans, on the SAME period as the cards above.
 *
 * That is the point of the table rather than a detail of it: plan
 * numbers have to reconcile with company numbers (spec 3.1), and they
 * only do if both are computed from one definition over one window.
 *
 * "Work done" counts projects whose service or schedule date falls in
 * the period. There is no completion timestamp anywhere in the schema -
 * a work order's stage is derived on read - so this is work SCHEDULED
 * in the window rather than work confirmed finished, and the column
 * header says "Work done" while this comment says what that means.
 * `sitesBilled` sits beside it as the harder number: work that reached
 * an invoice.
 */

const cellSx = {
  fontFamily: "Poppins, sans-serif",
  fontSize: "12px",
  padding: "10px 8px",
  textAlign: "left" as const,
  whiteSpace: "nowrap" as const,
};

const headSx = {
  ...cellSx,
  fontWeight: 600,
  color: themevals.colors.dkgrey,
  borderBottom: "1px solid " + themevals.colors.inputOutline,
};

const PlanTable: React.FC<{ plans: PlanRow[]; periodLabel: string }> = ({
  plans,
  periodLabel,
}) => {
  const navigate = useNavigate();

  return (
    <Panel
      title="Maintenance plans"
      subtitle={"Same period as the cards above: " + periodLabel}
      sx={{ marginBottom: "24px" }}
    >
      {plans.length === 0 ? (
        <EmptyState
          headline="No maintenance plans yet"
          detail="A plan turns one-off work into a program. Create one from a client's Plans tab and it appears here."
        />
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Box as="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <Box as="thead">
              <Box as="tr">
                <Box as="th" sx={headSx}>Plan</Box>
                <Box as="th" sx={headSx}>Sites</Box>
                <Box as="th" sx={headSx}>Work done</Box>
                <Box as="th" sx={headSx}>Sites billed</Box>
                <Box as="th" sx={headSx}>Billed</Box>
                <Box as="th" sx={headSx}>Collected</Box>
                <Box as="th" sx={headSx}>Margin</Box>
              </Box>
            </Box>
            <Box as="tbody">
              {plans.map((plan) => (
                <Box
                  as="tr"
                  key={plan.planId}
                  onClick={() => navigate("/maintenance/plans/" + plan.planId)}
                  sx={{
                    cursor: "pointer",
                    borderBottom: "1px solid",
                    borderColor: themevals.colors.inputOutline,
                    ":hover": { backgroundColor: "#F7F9FC" },
                  }}
                >
                  <Box as="td" sx={{ ...cellSx, fontWeight: 600, color: "base_800" }}>
                    {plan.name}
                    {plan.status === "closed" && (
                      <Box
                        as="span"
                        sx={{
                          marginLeft: "8px",
                          fontSize: "11px",
                          fontWeight: 500,
                          color: themevals.colors.dkgrey,
                        }}
                      >
                        Closed
                      </Box>
                    )}
                  </Box>
                  <Box as="td" sx={cellSx}>
                    {/* Committed count is the denominator when there is one;
                        the roster is context. Where no season is committed
                        the card says so rather than implying a commitment
                        nobody made (spec section 6). */}
                    {plan.committedSiteCount === null
                      ? plan.sites + " on the plan"
                      : plan.committedSiteCount + " committed of " + plan.sites}
                  </Box>
                  {/* Work SCHEDULED in the period. There is no completion
                      flag in the data, so this counts projects whose
                      service date falls inside the window - the same key
                      the calendar uses. "Sites billed" beside it is the
                      harder number. */}
                  <Box as="td" sx={cellSx}>{plan.workDone}</Box>
                  <Box as="td" sx={cellSx}>{plan.sitesBilled}</Box>
                  <Box as="td" sx={cellSx}>{currency(plan.billed)}</Box>
                  <Box as="td" sx={cellSx}>{currency(plan.collected)}</Box>
                  <Box
                    as="td"
                    sx={{
                      ...cellSx,
                      color:
                        plan.margin === null
                          ? themevals.colors.dkgrey
                          : plan.margin < 20
                            ? themevals.colors.danger
                            : themevals.colors.success,
                    }}
                  >
                    {/* Null margin means no billed work in the period. That
                        is not a margin of zero and must not read as one. */}
                    {plan.margin === null ? "No work billed" : plan.margin.toFixed(1) + "%"}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Panel>
  );
};

export default PlanTable;
